import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

export default function AdminReferralEvents() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'verified' | 'rejected' | 'paid'>('all');
  const queryClient = useQueryClient();
  const { session, isAdmin } = useAdminAuth();

  // Debug logging for session state
  console.log('🔍 AdminReferralEvents - Session state:', {
    hasSession: !!session,
    isAdmin,
    userId: session?.user?.id,
    accessToken: session?.access_token ? 'present' : 'missing'
  });

  const { data: events, isLoading, error, isError } = useQuery({
    queryKey: ['referral-events', statusFilter],
    enabled: !!session && isAdmin,
    queryFn: async () => {
      console.log('🚀 Starting referral_events query...');
      
      // Force session reload from localStorage
      const { data: { session: currentSession }, error: sessionError } = 
        await supabase.auth.getSession();
      
      console.log('🔐 Auth check:', {
        hasCurrentSession: !!currentSession,
        sessionError,
        userId: currentSession?.user?.id,
        accessToken: currentSession?.access_token ? 'present' : 'missing',
        tokenLength: currentSession?.access_token?.length
      });
      
      if (sessionError || !currentSession) {
        console.error('❌ Session error:', sessionError);
        throw new Error('No active session');
      }

      console.log('📊 Making database query with auth...');
      let query = supabase
        .from('referral_events')
        .select(`
          *,
          referral_types (name, display_name)
        `)
        .order('created_at', { ascending: false });
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query.limit(100);
      
      if (error) {
        console.error('❌ Query error:', error);
        throw error;
      }
      
      // Fetch referrer info separately for each event
      if (data && data.length > 0) {
        const codes = [...new Set(data.map(e => e.referral_code))];
        const { data: sources } = await supabase
          .from('referral_sources')
          .select('referral_code, user_id, User!referral_sources_user_id_fkey(email, firstName, lastName, preferredName)')
          .in('referral_code', codes);
        
        // Map referrer data to events
        if (sources) {
          const sourceMap = new Map(sources.map(s => [s.referral_code, s]));
          data.forEach((event: any) => {
            event.referrer = sourceMap.get(event.referral_code);
          });
        }
      }
      
      console.log('✅ Query successful, rows:', data?.length);
      return data;
    }
  });

  const verifyMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.rpc('verify_referral_event', {
        p_event_id: eventId,
        p_verified_by: user.id
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Event verified!');
      queryClient.invalidateQueries({ queryKey: ['referral-events'] });
    },
    onError: () => {
      toast.error('Failed to verify event');
    }
  });

  const statusColors = {
    pending: 'bg-yellow-500',
    verified: 'bg-green-500',
    rejected: 'bg-red-500',
    paid: 'bg-blue-500'
  };

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Referral Events</h1>
        <div className="flex gap-2">
          {(['all', 'pending', 'verified', 'paid'] as const).map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              onClick={() => setStatusFilter(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6">
              <div className="space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-40" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading referral events</AlertTitle>
          <AlertDescription>
            <div className="space-y-2">
              <p>{error?.message || 'Unknown error occurred'}</p>
              <details className="text-xs">
                <summary className="cursor-pointer">Technical details</summary>
                <pre className="mt-2 overflow-auto">{JSON.stringify(error, null, 2)}</pre>
              </details>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && events?.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No referral events found for the selected filter.</p>
        </Card>
      )}

      {!isLoading && !isError && events && events.length > 0 && (
        <div className="grid gap-4">
          {events.map((event: any) => (
            <Card key={event.id} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={statusColors[event.status as keyof typeof statusColors]}>
                      {event.status}
                    </Badge>
                    <h3 className="text-lg font-semibold">
                      {event.referral_types?.display_name}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground">Code: {event.referral_code}</p>
                  
                  {/* Who signed up */}
                  {event.metadata && typeof event.metadata === 'object' && 'firstName' in event.metadata && (
                    <div className="mt-2 p-2 bg-muted/50 rounded">
                      <p className="text-sm font-medium">Signup Details:</p>
                      <p className="text-sm text-muted-foreground">
                        {String(event.metadata.firstName)} {String(event.metadata.lastName)}
                      </p>
                      <p className="text-sm text-muted-foreground">{String(event.metadata.email)}</p>
                      {'profession' in event.metadata && (
                        <p className="text-sm text-muted-foreground">
                          {String(event.metadata.profession)}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Referrer info */}
                  {event.referrer?.User && (
                    <div className="mt-2 p-2 bg-primary/5 rounded">
                      <p className="text-sm font-medium">Referred by:</p>
                      <p className="text-sm text-muted-foreground">
                        {event.referrer.User.preferredName || event.referrer.User.firstName} {event.referrer.User.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {event.referrer.User.email}
                      </p>
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground mt-2">
                    Amount: ${event.commission_amount}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Created: {new Date(event.created_at).toLocaleDateString()}
                  </p>
                  {event.entity_type && (
                    <p className="text-sm text-muted-foreground">
                      Type: {event.entity_type.replace(/_/g, ' ')}
                    </p>
                  )}
                </div>
                {event.status === 'pending' && (
                  <Button
                    onClick={() => verifyMutation.mutate(event.id)}
                    disabled={verifyMutation.isPending}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Verify
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
